import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { Company } from "../lib/access";
import { Numbers } from "./Numbers";

interface Call {
  id: string;
  started_at: string;
  from_e164: string | null;
  to_e164: string | null;
  status: "ringing" | "answered" | "missed" | "voicemail" | "failed" | "busy";
  duration_seconds: number | null;
  handled_by: string | null;
  department_id: string | null;
  outcome: string | null;
  notes: string | null;
}

const STATUS_WORDS: Record<Call["status"], string> = {
  ringing: "Ringing",
  answered: "Answered",
  missed: "Missed",
  voicemail: "Voicemail",
  failed: "Failed",
  busy: "Busy",
};

function duration(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function when(iso: string, timezone: string | null): string {
  return new Date(iso).toLocaleString(undefined, {
    timeZone: timezone ?? undefined,
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * The call list.
 *
 * Everything here is filtered by the database, not by this component: a call
 * this person may not see never arrives, and the recording of a call they may
 * see but not hear arrives without its audio. So there is no permission logic
 * in this file, and there should never be any — the browser is not where that
 * question gets answered.
 */
export function Calls({ company }: { company: Company }) {
  const [tab, setTab] = useState<"calls" | "numbers">("calls");
  const [calls, setCalls] = useState<Call[] | null>(null);
  const [depts, setDepts] = useState<Record<string, string>>({});
  const [people, setPeople] = useState<Record<string, string>>({});
  const [audible, setAudible] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Call | null>(null);
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [c, d, m] = await Promise.all([
      supabase
        .from("calls")
        .select("id,started_at,from_e164,to_e164,status,duration_seconds,handled_by,department_id,outcome,notes")
        .eq("company_id", company.id)
        .order("started_at", { ascending: false })
        .limit(200),
      supabase.from("departments").select("id,name").eq("company_id", company.id),
      supabase
        .from("memberships")
        .select("user_id, profiles!inner(id, full_name)")
        .eq("company_id", company.id),
    ]);

    const rows = (c.data as Call[]) ?? [];
    setCalls(rows);
    setDepts(Object.fromEntries(((d.data as { id: string; name: string }[]) ?? []).map((x) => [x.id, x.name])));
    setPeople(
      Object.fromEntries(
        ((m.data as unknown as { user_id: string; profiles: { full_name: string | null } }[]) ?? [])
          .map((x) => [x.user_id, x.profiles?.full_name ?? "Someone"]),
      ),
    );

    // Which of these calls this person may actually hear. We ask for the rows
    // and see which come back rather than working it out here — the policy on
    // call_recordings is the whole of requirement 10, and duplicating it in
    // TypeScript would mean maintaining it in two places.
    if (rows.length > 0) {
      const { data: recs } = await supabase
        .from("call_recordings")
        .select("call_id")
        .in("call_id", rows.map((r) => r.id));
      setAudible(new Set(((recs as { call_id: string }[]) ?? []).map((r) => r.call_id)));
    } else {
      setAudible(new Set());
    }
  }, [company.id]);

  useEffect(() => { void load(); }, [load]);

  /**
   * Ask the API for a playback link. The link lasts sixty seconds, so it is
   * fetched at the moment somebody presses play and never held in this
   * component's state longer than the audio element needs it.
   */
  async function play(callId: string) {
    setMsg(null);
    setPlaying(callId);
    setAudioUrl(null);

    const { data: sess } = await supabase.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_API_URL}/recordings/${callId}/link`, {
      method: "POST",
      headers: { Authorization: `Bearer ${sess.session?.access_token ?? ""}` },
    });

    if (!res.ok) {
      setPlaying(null);
      setMsg(
        res.status === 409
          ? "That recording is still being fetched. Try again in a minute."
          : "That recording could not be played.",
      );
      return;
    }

    const body = (await res.json()) as { url: string };
    setAudioUrl(`${import.meta.env.VITE_API_URL}${body.url}`);
  }

  function startEditing(call: Call) {
    setEditing(call);
    setOutcome(call.outcome ?? "");
    setNotes(call.notes ?? "");
    setMsg(null);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setMsg(null);
    const { error } = await supabase
      .from("calls")
      .update({ outcome: outcome || null, notes: notes || null })
      .eq("id", editing.id);

    // RLS filters the row out rather than raising, so a silent no-op is the
    // shape a refusal takes here. Reload and let the list tell the truth.
    if (error) {
      setMsg("That could not be saved.");
      return;
    }
    setEditing(null);
    void load();
  }

  if (tab === "numbers") {
    return (
      <>
        <Tabs tab={tab} setTab={setTab} />
        <Numbers company={company} />
      </>
    );
  }

  return (
    <>
      <header className="page-head">
        <h1>Calls</h1>
        <p className="muted">
          Every call to your numbers, including the ones nobody answered — those are
          the ones worth chasing.
        </p>
      </header>

      <Tabs tab={tab} setTab={setTab} />

      {msg && !editing && <p className="status">{msg}</p>}

      {editing && (
        <form className="card" onSubmit={save}>
          <h2>
            {editing.from_e164 ?? "Unknown number"} · {when(editing.started_at, company.timezone)}
          </h2>
          <label htmlFor="oc">What came of it</label>
          <input id="oc" value={outcome} placeholder="Booked an appointment"
            onChange={(e) => setOutcome(e.target.value)} />
          <label htmlFor="nt">Notes</label>
          <textarea id="nt" rows={3} value={notes}
            onChange={(e) => setNotes(e.target.value)} />
          <div className="row-form">
            <button className="btn" type="submit">Save</button>
            <button className="btn ghost small" type="button" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
          {msg && <p className="status">{msg}</p>}
        </form>
      )}

      {calls === null ? (
        <p className="muted">Loading…</p>
      ) : calls.length === 0 ? (
        <div className="card">
          <p className="muted">
            No calls yet. Once a number is live and pointed at a department, calls
            appear here as they ring.
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>When</th><th>From</th><th>Department</th><th>Answered by</th>
                <th>Status</th><th>Length</th><th></th>
              </tr>
            </thead>
            <tbody>
              {calls.map((c) => (
                <tr key={c.id}>
                  <td>{when(c.started_at, company.timezone)}</td>
                  <td>
                    <b>{c.from_e164 ?? "Withheld"}</b>
                    {c.outcome && <span className="sub">{c.outcome}</span>}
                  </td>
                  <td className="muted">
                    {c.department_id ? depts[c.department_id] ?? "—" : "—"}
                  </td>
                  <td className="muted">
                    {c.handled_by ? people[c.handled_by] ?? "Someone" : "—"}
                  </td>
                  <td>
                    <span className={"state " + c.status}>{STATUS_WORDS[c.status]}</span>
                  </td>
                  <td className="muted">
                    {duration(c.duration_seconds)}
                    {playing === c.id && audioUrl && (
                      <audio className="player" src={audioUrl} controls autoPlay />
                    )}
                  </td>
                  <td className="right">
                    {audible.has(c.id) && playing !== c.id && (
                      <button className="btn ghost small" onClick={() => void play(c.id)}>
                        Play
                      </button>
                    )}
                    <button className="btn ghost small" onClick={() => startEditing(c)}>
                      Notes
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Tabs({ tab, setTab }: { tab: string; setTab: (t: "calls" | "numbers") => void }) {
  return (
    <div className="tabs">
      <button className={tab === "calls" ? "on" : ""} onClick={() => setTab("calls")}>
        Calls
      </button>
      <button className={tab === "numbers" ? "on" : ""} onClick={() => setTab("numbers")}>
        Numbers and routing
      </button>
    </div>
  );
}
